import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { HorizontalSeparator } from '@clds/separator';
import { globalTheme } from '@clds/component-theme';
import { Add, OpenInNew } from '@clds/icon';
import { Chip } from '@clds/chip';
import { AppId, ExpandableMenuItem as ExpandableMenuSection, NavigationContainer } from '@cld-remote/application_wrapper/index';

import { useMessage } from '../../i18n/useMessage';
import { useTrackEvent } from '../../hooks/analytics.hooks';
import { createConsoleUrl, createVideoUrl } from '../../utils/console';
import { SideMenuLink } from './side-menu-link';
import cloudinaryAssetsSvg from './icons/CloudinaryAssets.svg';
import videoAppLightSvg from './icons/VideoAppLight.svg';
import videoAppDarkSvg from './icons/VideoAppDark.svg';
import { modules } from './side-menu.constants';
import { useExpandedSectionsMap } from './useExpandedSectionsMap';
import { SideMenuTooltipButton } from './side-menu-tooltip-buton';
import { useAppData } from '../../context/app-context';
import { TreatmentValue } from '../../types/treatments';
import { UnicornApi } from '@cld/unicorn-api';
import { GettingStartedIcon } from './icons/GettingStartedIcon';
import { DashboardIcon } from './icons/DashboardIcon';
import { ReportsIcon } from './icons/ReportsIcon';
import { FileUploadIcon } from './icons/FileUploadIcon';
import { TransformIcon } from './icons/TransformIcon';
import { GaugeIcon } from './icons/GaugeIcon';
import { VideoIcon } from './icons/VideoIcon';

const IconImage = styled.img<{ size: 'md' | 'sm' }>`
  height: ${({ size }) => globalTheme.font.size[size]};
  width: ${({ size }) => globalTheme.font.size[size]};
  border-radius: ${(props) => props.theme.radius.md};
  vertical-align: center;
`;

const SideBarMenuStateKey = '@cld/console/side-bar-menu-state';

export const SideMenuContent = ({
  optimizationReportsLinkTreatment,
  mediaExplorerDeprecationTreatment,
  themeName,
}: {
  optimizationReportsLinkTreatment: TreatmentValue;
  mediaExplorerDeprecationTreatment: TreatmentValue;
  themeName: UnicornApi['data']['theme']['name'];
}) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent('side menu');
  const t = useMessage();
  const { features } = useAppData();

  const {
    isAllowedForDashboard,
    isAllowedForGettingStartedPage,
    isAllowedForTransformations,
    isAllowedForReports,
    isAllowedForMediaLibrary,
    isAllowedForMediaExplorer,
    isAllowedForVideoPages,
    isAllowedForNewVideoApp,
    isNewVideoReportsPageEnabled,
    isNewErrorReportsPageEnabled,
    isAllowedForMonthlyValueReport,
    usedNewDashboardBeforeFullRelease,
  } = features;

  const shouldDisplayMediaExplorer = isAllowedForMediaExplorer && mediaExplorerDeprecationTreatment === 'off';

  const getLinkProps = (config: { to: string; label: string; reloadDocument?: boolean; target?: '_blank' | '_self' }) => {
    const { to, label, target, reloadDocument } = config;
    return {
      to,
      onClick: () => {
        trackEvent(label, 'navigate');
        if (reloadDocument) {
          window.open(to, target);
        } else {
          navigate(to);
        }
      },
    };
  };

  const expandedSectionConfig = useMemo(
    () => ({
      pathname: window.location.pathname,
      defaultValue: {
        reports: false,
        manageAndAnalyze: true,
        optimizeAndDeliver: true,
        video: true,
        transformAndCustomize: true,
      },
      storageKey: SideBarMenuStateKey,
    }),
    []
  );

  const [expandedSectionsMap, setExpandedSection] = useExpandedSectionsMap(expandedSectionConfig, trackEvent);
  return (
    <NavigationContainer>
      {isAllowedForGettingStartedPage && (
        <SideMenuLink
          activeOnPath="exact-and-subpaths"
          {...getLinkProps({
            to: modules.developerGettingStarted,
            label: 'getting started',
          })}
          leftSlot={<GettingStartedIcon />}
        >
          {t('getting-started')}
        </SideMenuLink>
      )}
      {isAllowedForDashboard && (
        <SideMenuLink
          activeOnPath="exact-and-subpaths"
          {...getLinkProps({
            to: modules.dashboard,
            label: 'dashboard',
          })}
          leftSlot={<DashboardIcon />}
        >
          {t('dashboard')}
        </SideMenuLink>
      )}
      {isAllowedForReports && (
        <ExpandableMenuSection
          label={t('reports')}
          leftSlot={<ReportsIcon />}
          data-testId="reports"
          isExpanded={expandedSectionsMap.reports}
          onExpandedChange={(newExpandedStatus) => {
            setExpandedSection('reports', newExpandedStatus);
          }}
        >
          {!usedNewDashboardBeforeFullRelease && (
            <SideMenuLink
              activeOnPath="exact-only"
              {...getLinkProps({
                to: modules.usageReports,
                label: 'usage reports',
              })}
            >
              {t('usage-reports')}
            </SideMenuLink>
          )}
          <SideMenuLink
            activeOnPath="exact-only"
            {...getLinkProps({
              to: modules.deliveryReports,
              label: 'delivery reports',
            })}
          >
            {t('delivery-reports')}
          </SideMenuLink>
          <SideMenuLink
            activeOnPath="exact-only"
            {...(isNewErrorReportsPageEnabled
              ? getLinkProps({ to: modules.newErrorReports, label: 'new error reports' })
              : getLinkProps({ to: modules.errorReports, label: 'error reports' }))}
          >
            {t('error-reports')}
          </SideMenuLink>
          {isAllowedForMonthlyValueReport && (
            <SideMenuLink
              activeOnPath="exact-only"
              {...getLinkProps({
                to: modules.monthlyValueReports,
                label: 'monthly value reports',
              })}
            >
              {t('monthly-value-reports')}
            </SideMenuLink>
          )}
        </ExpandableMenuSection>
      )}

      {(shouldDisplayMediaExplorer || isAllowedForMediaLibrary) && <HorizontalSeparator horizontalMargin={'sm'} verticalMargin="sm" />}

      {(shouldDisplayMediaExplorer || isAllowedForMediaLibrary) && (
        <ExpandableMenuSection
          label={t('manage-and-analyze')}
          data-testId="manage-and-analyze"
          isExpanded={expandedSectionsMap.manageAndAnalyze}
          leftSlot={<FileUploadIcon />}
          onExpandedChange={(newExpandedStatus) => {
            setExpandedSection('manageAndAnalyze', newExpandedStatus);
          }}
        >
          {shouldDisplayMediaExplorer && (
            <SideMenuLink
              activeOnPath="exact-and-subpaths"
              {...getLinkProps({
                to: modules.mediaExplorer,
                label: 'media explorer',
              })}
            >
              {t('media-explorer')}
            </SideMenuLink>
          )}
          {isAllowedForMediaLibrary && (
            <SideMenuLink
              activeOnPath="exact-and-subpaths"
              {...getLinkProps({
                to: createConsoleUrl('media_library/search', customerId, { 'cld-target-product': AppId.DigitalAssetManagement }),
                label: 'media library',
                target: '_blank',
                reloadDocument: true,
              })}
              leftSlot={<IconImage size="sm" src={cloudinaryAssetsSvg} />}
              rightSlot={<OpenInNew size="sm" />}
            >
              {t('media-library')}
            </SideMenuLink>
          )}
        </ExpandableMenuSection>
      )}

      {isAllowedForTransformations && (
        <>
          <HorizontalSeparator horizontalMargin={'sm'} verticalMargin="sm" />
          <ExpandableMenuSection
            label={t('transform-and-customize')}
            data-testId="transform-and-customize"
            isExpanded={expandedSectionsMap.transformAndCustomize}
            leftSlot={<TransformIcon />}
            onExpandedChange={(newExpandedStatus) => {
              setExpandedSection('transformAndCustomize', newExpandedStatus);
            }}
          >
            <SideMenuLink
              activeOnPath="exact-and-subpaths"
              {...getLinkProps({
                to: modules.transformationsCenter,
                label: 'transformations center',
              })}
            >
              {t('transformations-center')}
            </SideMenuLink>
            <SideMenuLink
              activeOnPath="exact-and-subpaths"
              {...getLinkProps({
                to: modules.transformationsManage,
                label: 'manage transformations',
              })}
              rightSlot={
                <SideMenuTooltipButton
                  // @ts-expect-error TODO: Silencing existing error, please fix it
                  parentPath={modules.transformationsManage}
                  parentActiveOnPath="exact-and-subpaths"
                  icon={<Add />}
                  tooltip={t('new-transformation')}
                  action={
                    getLinkProps({
                      to: modules.transformationBuilder,
                      label: 'new transformation',
                    }).onClick
                  }
                />
              }
            >
              {t('transformations-manage')}
            </SideMenuLink>
          </ExpandableMenuSection>
        </>
      )}

      <HorizontalSeparator horizontalMargin={'sm'} verticalMargin="sm" />
      <ExpandableMenuSection
        label={t('optimize-and-deliver')}
        data-testId="optimize-and-deliver"
        isExpanded={expandedSectionsMap.optimizeAndDeliver}
        leftSlot={<GaugeIcon />}
        onExpandedChange={(newExpandedStatus) => {
          setExpandedSection('optimizeAndDeliver', newExpandedStatus);
        }}
      >
        <SideMenuLink
          activeOnPath="exact-and-subpaths"
          data-testId="optimization-center"
          {...getLinkProps({
            to: modules.optimizationCenter,
            label: 'overview optimization',
          })}
        >
          {t('optimization-center-menu-label')}
        </SideMenuLink>
        {optimizationReportsLinkTreatment === 'on' && (
          <SideMenuLink
            activeOnPath="exact-and-subpaths"
            data-testId="optimization-reports"
            {...getLinkProps({
              to: modules.optimizationReports,
              label: 'reports optimization',
            })}
          >
            {t('optimization-reports-menu-label')}
          </SideMenuLink>
        )}
      </ExpandableMenuSection>

      {isAllowedForVideoPages && isAllowedForNewVideoApp && (
        <>
          <HorizontalSeparator horizontalMargin={'sm'} verticalMargin="sm" />
          <SideMenuLink
            activeOnPath="exact-and-subpaths"
            data-testId="video-app-link"
            {...getLinkProps({
              to: createVideoUrl('', customerId),
              label: 'video app',
              target: '_self',
              reloadDocument: true,
            })}
            leftSlot={<IconImage size="sm" src={themeName === 'dark' ? videoAppDarkSvg : videoAppLightSvg} />}
            rightSlot={<Chip label={t('video-app-link-tag')} variant="primary" size="xxs" />}
          >
            {t('video-app-link')}
          </SideMenuLink>
        </>
      )}
    </NavigationContainer>
  );
};
